import React, { createContext, ReactNode, useContext } from "react";

import { skipToken } from "@reduxjs/toolkit/query";

import {
  ParsedError,
  parseError,
  useGetGvmUpgradesQuery,
  useGetOrderQuery,
  useGetVehiclesQuery,
} from "../api/wordpress";
import { Woocommerce } from "../types/Order.ts";
import { WordPressProduct } from "../types/Product";
import { Vehicle } from "../types/Vehicle";
import { ConfigurationContext } from "./ConfigurationContext";

interface BookingContextProps {
  vehicles: Vehicle[];
  gvmUpgrades: WordPressProduct[];
  isLoading: boolean;
  error: any;
  order: Woocommerce.Order | undefined;
  isLoadingOrder: boolean;
  orderError: ParsedError | null;
}

const BookingContext = createContext<BookingContextProps | undefined>(
  undefined,
);

interface BookingProviderProps {
  children: ReactNode;
}

export const BookingProvider: React.FC<BookingProviderProps> = ({
  children,
}) => {
  const initial = useContext(ConfigurationContext);

  const {
    data: vehicles,
    isLoading: isLoadingVehicles,
    error: vehiclesError,
  } = useGetVehiclesQuery();

  const {
    data: gvmUpgrades,
    isLoading: isLoadingGvmUpgrades,
    error: gvmUpgradesError,
  } = useGetGvmUpgradesQuery();

  const {
    data: order,
    isLoading: isLoadingOrder,
    error: orderError,
  } = useGetOrderQuery(initial.orderId ?? skipToken);

  const value: BookingContextProps = {
    vehicles: vehicles || [],
    gvmUpgrades: gvmUpgrades || [],
    isLoading: isLoadingVehicles || isLoadingGvmUpgrades,
    error: vehiclesError || gvmUpgradesError,
    order,
    isLoadingOrder,
    orderError: orderError ? parseError(orderError) : null,
  };

  return (
    <BookingContext.Provider value={value}>{children}</BookingContext.Provider>
  );
};

export const useBooking = (): BookingContextProps => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error("useBooking must be used within a BookingProvider");
  }
  return context;
};
